/**
 * Onay kuyruğu istemcisi (`GET /api/approvals`).
 *
 * Bekleyen BÜTÜN kalemler tek listede gelir; tür süzgeci istemci tarafındadır (sunucu `kind`
 * süzgeci kabul etmez). Karar uçları kuyruğun YANINDA yaşar: her türün kararı kendi modülünde
 * (`/reload-requests/:id/decide`, `/orders/transfer-requests/:id/decide`,
 * `/delivery-deviations/:id/decide`, `/trip-audits/:id/decide`) — burada yalnız yol ve gövde
 * eşlemesi tutulur, mantık panelde değil sunucuda.
 *
 * Karar ucu olmayan türlerde `APPROVAL_DECISIONS` girdisi `null`'dur; ekran o satırda yalnız
 * "detaya git" bağlantısı gösterir.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '@aquabox/shared';
import { api } from './client';

export type ApprovalKind =
  | 'RELOAD'
  | 'CASH_HANDOVER'
  | 'STOCK_CORRECTION'
  | 'MORNING_LOAD'
  | 'ORDER_TRANSFER'
  | 'DELIVERY_DEVIATION'
  | 'TRIP_AUDIT';

export interface ApprovalItem {
  id: string;
  kind: ApprovalKind;
  status: string;
  /** Sunucunun yazdığı kısa başlık. */
  title: string;
  /** Sunucunun yazdığı detay metni (özet sütunu). */
  detail: string | null;
  /** Para alanı STRING'dir — panel hesap yapmaz, yalnız gösterir. */
  amount: string | null;
  qty: number | null;
  /** UTC ISO — ekran İstanbul saatine çevirir. */
  createdAt: string;
  tripId: string | null;
  reloadRequestId: string | null;
  orderId: string | null;
  targetVehicleId: string | null;
  deliveryId: string | null;
  trip?: {
    id: string;
    vehicle: { plate: string } | null;
    driver: { user: { fullName: string } | null } | null;
  } | null;
  reloadRequest?: {
    id: string;
    items: { product: { name: string } }[];
  } | null;
  order?: {
    id: string;
    customerName: string | null;
    customer: { fullName: string } | null;
    vehicle: { plate: string } | null;
  } | null;
  targetVehicle?: { id: string; plate: string } | null;
}

export interface ApprovalPage {
  items: ApprovalItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** Bir türün karar ucu ve gövde biçimi. `null` → karar ucu yok (yalnız detaya git). */
export interface ApprovalDecision {
  /** Karar ucu; RELOAD için talep id'si gerekir (ApprovalItem id'si DEĞİL). */
  path: (item: ApprovalItem) => string | null;
  approveBody: Record<string, unknown>;
  rejectBody: (note: string) => Record<string, unknown>;
  /** Reddetme gerekçesinin en az karakter sayısı (sunucunun kendi eşiği). */
  minRejectNote: number;
  /**
   * Gerekçe uzunluğunu SAYMA kuralı. Varsayılan: trim'li uzunluk.
   *
   * DELIVERY_DEVIATION ve TRIP_AUDIT sunucuda BOŞLUKSUZ karakter sayar
   * (`note.replace(/\s/g,'').length >= 10`) — panelde trim uzunluğu sayılırsa kullanıcı
   * düğmeyi açar, sunucu 400 döner. Bu yüzden iki tür kendi sayacını taşır.
   */
  countNote?: (note: string) => number;
}

/**
 * Gerekçe uzunluğu — sunucunun sayma kuralıyla AYNI.
 *
 * Panel kendi kuralını uydurursa ya da sunucudan farklı sayarsa düğme yanlış açılır/kapalı
 * kalır; tek doğruluk kaynağı sunucu şemasıdır (`delivery-deviations.schema.ts`,
 * `trip-audit.schema.ts`, `trips.schema.ts`, `orders.schema.ts`).
 */
export function rejectNoteLength(spec: ApprovalDecision, note: string): number {
  return spec.countNote ? spec.countNote(note) : note.trim().length;
}

export const APPROVAL_DECISIONS: Record<ApprovalKind, ApprovalDecision | null> = {
  // `POST /api/reload-requests/:id/decide` — gövde `{ approve, rejectReason }`.
  RELOAD: {
    path: (item) =>
      item.reloadRequestId ? `/api/reload-requests/${item.reloadRequestId}/decide` : null,
    approveBody: { approve: true },
    rejectBody: (note) => ({ approve: false, rejectReason: note }),
    minRejectNote: 3,
  },
  // `POST /api/orders/transfer-requests/:approvalId/decide` — gövde `{ decision, note }`.
  ORDER_TRANSFER: {
    path: (item) => `/api/orders/transfer-requests/${item.id}/decide`,
    approveBody: { decision: 'APPROVE' },
    rejectBody: (note) => ({ decision: 'REJECT', note }),
    minRejectNote: 3,
  },
  // `POST /api/delivery-deviations/:approvalId/decide` — REVIEWED = incelendi (onay).
  DELIVERY_DEVIATION: {
    path: (item) => `/api/delivery-deviations/${item.id}/decide`,
    approveBody: { decision: 'REVIEWED' },
    rejectBody: (note) => ({ decision: 'REJECT', note }),
    minRejectNote: 10,
    // Sunucu: `note.replace(/\s/g,'').length >= 10` (delivery-deviations.schema.ts).
    countNote: (note) => note.replace(/\s/g, '').length,
  },
  // `POST /api/trip-audits/:approvalId/decide` — CONFIRMED = süphe haklı (red).
  TRIP_AUDIT: {
    path: (item) => `/api/trip-audits/${item.id}/decide`,
    approveBody: { decision: 'REVIEWED' },
    rejectBody: (note) => ({ decision: 'CONFIRMED', note }),
    minRejectNote: 10,
    // Sunucu: `note.replace(/\s/g,'').length >= 10` (trip-audit.schema.ts).
    countNote: (note) => note.replace(/\s/g, '').length,
  },
  // Bu üç türün kuyrukta karar ucu yok: CASH_HANDOVER türetilmiş bir satırdır
  // (dashboard), STOCK_CORRECTION ve MORNING_LOAD henüz yazılmıyor.
  CASH_HANDOVER: null,
  STOCK_CORRECTION: null,
  MORNING_LOAD: null,
};

/**
 * Sunucunun sayfa boyutu üst sınırı: `pageSchema` (`apps/api/src/common/zod.pipe.ts`)
 * `pageSize` için 1..200 kabul eder. Panel bu sınırı ister — varsayılan 50'de kuyruk
 * sessizce kesilir, kullanıcı eksik kalanı göremez.
 */
const APPROVALS_PAGE_SIZE = 200;

export function useApprovals() {
  return useQuery({
    queryKey: ['approvals'],
    queryFn: () => api.get<ApprovalPage>(`/api/approvals?pageSize=${APPROVALS_PAGE_SIZE}`),
  });
}

export interface ApprovalDecisionInput {
  item: ApprovalItem;
  approve: boolean;
  /** Reddetmede gerekçe (sunucunun eşiği kadar uzun olmalı). */
  note?: string;
}

/**
 * Karar mutation'ı. Karar ucu yoksa istek ATILMAZ — çağıran ekran o satırda düğme göstermez.
 */
export function useApprovalDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApprovalDecisionInput) => {
      const spec = APPROVAL_DECISIONS[input.item.kind];
      if (!spec) throw new Error(t('panel.approvals.noDecisionEndpoint'));
      const path = spec.path(input.item);
      if (!path) throw new Error(t('panel.approvals.noDecisionEndpointForItem'));
      const body = input.approve
        ? spec.approveBody
        : spec.rejectBody((input.note ?? '').trim());
      return api.post(path, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['approvals'] });
      await qc.invalidateQueries({ queryKey: ['approvals-count'] });
    },
  });
}
